import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBanners } from '../../hooks/useBanners';
import { safeImageUrl } from '../../utils/url';
import { HomeBanner } from '../../models';

const AUTO_ADVANCE_INTERVAL_MS = 6000;
const INTERACTION_PAUSE_MS = 12000;
const SWIPE_THRESHOLD_PX = 40;
const SWIPE_AXIS_RATIO = 1.5;
const MOBILE_MEDIA_QUERY = '(max-width: 639px)';

interface HomeBannersProps {
  onSelectCollection: (collectionId: number) => void;
}

function HomeBannersSkeleton() {
  return <div className="aspect-square sm:aspect-[3/1] rounded-2xl bg-champagne animate-pulse" />;
}

interface BannerSlideProps {
  banner: HomeBanner;
  useMobileImage: boolean;
  alt: string;
  priority: boolean;
  onSelectCollection: (id: number) => void;
}

function BannerImage({
  banner,
  useMobileImage,
  alt,
  priority,
}: {
  banner: HomeBanner;
  useMobileImage: boolean;
  alt: string;
  priority: boolean;
}) {
  return (
    <picture>
      {useMobileImage && banner.mobileImageUrl && (
        <source media={MOBILE_MEDIA_QUERY} srcSet={safeImageUrl(banner.mobileImageUrl)} />
      )}
      <img
        src={safeImageUrl(banner.imageUrl ?? banner.mobileImageUrl ?? '')}
        alt={alt}
        draggable={false}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className="w-full h-full object-cover"
      />
    </picture>
  );
}

function BannerSlide({ banner, useMobileImage, alt, priority, onSelectCollection }: BannerSlideProps) {
  const image = <BannerImage banner={banner} useMobileImage={useMobileImage} alt={alt} priority={priority} />;
  if (banner.linkType === 1 && banner.linkTargetId) {
    return (
      <Link to={`/produtos/${banner.linkTargetId}`} className="block w-full h-full">
        {image}
      </Link>
    );
  }

  if (banner.linkType === 2 && banner.linkTargetId) {
    return (
      <button
        type="button"
        onClick={() => onSelectCollection(banner.linkTargetId as number)}
        className="block w-full h-full text-left"
      >
        {image}
      </button>
    );
  }

  if (banner.linkType === 3 && banner.linkUrl) {
    return (
      <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
        {image}
      </a>
    );
  }

  return (
    <div className="w-full h-full">
      {image}
    </div>
  );
}

export function HomeBanners({ onSelectCollection }: HomeBannersProps) {
  const { banners, isLoading, error, isEmpty } = useBanners();
  const [currentIndex, setCurrentIndex] = useState(0);
  const isHoveredRef = useRef(false);
  const pausedUntilRef = useRef(0);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const hasMultiple = banners.length > 1;
  const useMobileImages = banners.length > 0 && banners.every((b) => Boolean(b.mobileImageUrl));

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  }, [banners.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  }, [banners.length]);

  const pauseForInteraction = useCallback(() => {
    pausedUntilRef.current = Date.now() + INTERACTION_PAUSE_MS;
  }, []);

  useEffect(() => {
    if (!hasMultiple) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) return;

    const id = window.setInterval(() => {
      if (document.hidden || isHoveredRef.current || Date.now() < pausedUntilRef.current) return;
      goToNext();
    }, AUTO_ADVANCE_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [hasMultiple, goToNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    pauseForInteraction();
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    pauseForInteraction();
    if (!start || !hasMultiple) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_AXIS_RATIO) return;
    if (deltaX < 0) goToNext();
    else goToPrev();
  };

  const handleManualNavigation = (action: () => void) => {
    pauseForInteraction();
    action();
  };

  if (error || isEmpty) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 pt-4 md:pt-6">
      {isLoading ? (
        <HomeBannersSkeleton />
      ) : (
        <div
          role="region"
          aria-roledescription="carrossel"
          aria-label="Destaques da loja"
          className={`relative rounded-2xl overflow-hidden shadow-sm touch-pan-y ${useMobileImages ? 'aspect-square' : 'aspect-[3/1]'} sm:aspect-[3/1] bg-champagne-light`}
          onMouseEnter={() => { isHoveredRef.current = true; }}
          onMouseLeave={() => { isHoveredRef.current = false; }}
          onPointerDown={pauseForInteraction}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex w-full h-full transition-transform duration-700 ease-in-out motion-reduce:transition-none"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {banners.map((banner, idx) => (
              <div key={banner.id} className="w-full h-full shrink-0" inert={idx !== currentIndex}>
                <BannerSlide
                  banner={banner}
                  useMobileImage={useMobileImages}
                  alt={`Banner ${idx + 1} de ${banners.length}`}
                  priority={idx === 0}
                  onSelectCollection={onSelectCollection}
                />
              </div>
            ))}
          </div>

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={() => handleManualNavigation(goToPrev)}
                aria-label="Banner anterior"
                className="hidden [@media(hover:hover)]:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark items-center justify-center shadow-sm transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleManualNavigation(goToNext)}
                aria-label="Próximo banner"
                className="hidden [@media(hover:hover)]:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark items-center justify-center shadow-sm transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center bg-black/25 rounded-full px-1">
                {banners.map((b, idx) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => handleManualNavigation(() => setCurrentIndex(idx))}
                    aria-label={`Ir para o banner ${idx + 1}`}
                    aria-current={idx === currentIndex}
                    className="w-6 h-6 flex items-center justify-center"
                  >
                    <span
                      className={`h-1.5 rounded-full transition-all duration-200 ${
                        idx === currentIndex ? 'w-3.5 bg-white' : 'w-1.5 bg-white/60'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
